import { useEffect, useState } from "react";
import api from "../config/api";

// Shared, module-cached store settings so many components can read brand/contact/
// homepage config from a single network request instead of each fetching.
//
// Also persisted to localStorage so the brand (name/logo/favicon) is available
// on the VERY FIRST render of a page load — otherwise components briefly show a
// fallback (e.g. the admin header's generic icon) while /api/settings loads.
const LS_KEY = "site_settings_cache";
let cache = null;
try {
  const stored = localStorage.getItem(LS_KEY);
  if (stored) cache = JSON.parse(stored);
} catch (e) {
  /* ignore malformed/unavailable storage */
}
let inflight = null;
const listeners = new Set();

function load() {
  if (!inflight) {
    inflight = api
      .get("/api/settings")
      .then(({ data }) => {
        cache = data || {};
        try {
          localStorage.setItem(LS_KEY, JSON.stringify(cache));
        } catch (e) {
          /* storage full/unavailable — fine */
        }
        listeners.forEach((fn) => fn(cache));
        return cache;
      })
      .catch(() => {
        cache = cache || {}; // keep any localStorage copy on network failure
        return cache;
      });
  }
  return inflight;
}

export default function useSiteSettings() {
  const [settings, setSettings] = useState(cache || {});

  useEffect(() => {
    let active = true;
    const update = (s) => {
      if (active) setSettings(s);
    };
    listeners.add(update);
    if (cache) update(cache); // instant paint from cache (localStorage or prior fetch)
    load().then(update); // always refresh from the server (deduped via `inflight`)
    return () => {
      active = false;
      listeners.delete(update);
    };
  }, []);

  return settings;
}
